// ─── INTERNAL LOGIN ───────────────────────────────────────────────────────────
// /internal — the passphrase gate for Nous Topology access. No account, no
// Supabase session, no platform chrome. One field.
//
// ⚠⚠ THIS EXISTS BECAUSE THE PASSPHRASE CANNOT LIVE IN THE BUNDLE. Internal
// access used to depend entirely on src/internal.js, which is gitignored — so
// the production build has no passphrase in it, `_internalModule` is undefined,
// and internal mode was unreachable on quant-signal.ai by construction. The
// credential now travels the other way: the user types it, the SERVER checks it
// against NOUS_INTERNAL_PASSPHRASE from the Railway environment, and the client
// only ever holds the 12h token that comes back.
//
// ⚠ NOT LINKED FROM ANYWHERE — no nav item, no button, no footer. Obscurity is
// not the security here (the passphrase is), but there is no reason to advertise
// the door either. To a visitor who stumbles on it this is an unlabelled login.
//
// ⚠ The passphrase is never logged, never put in a URL, never stored. It exists
// in component state for the duration of one submit and goes out in a POST body.

import { useState, useRef, useEffect } from "react";
import { C, s, font, NousIcon, INTERNAL_TOKEN_KEY } from "../core";

const MAX_LEN = 100;

export function InternalLogin() {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  // ⚠ autoFocus alone is unreliable when the element mounts inside a page that
  // is itself the first render; focusing on mount is the version that holds.
  useEffect(() => {
    try { inputRef.current?.focus(); } catch { /* not fatal */ }
  }, []);

  const fail = (msg) => {
    setError(msg);
    setValue("");
    try { inputRef.current?.focus(); } catch { /* not fatal */ }
  };

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (busy || !value) return;
    setBusy(true);
    setError(null);

    const backend = window.QUANT_SIGNAL_BACKEND;
    if (!backend) {
      setBusy(false);
      fail("Could not reach the platform. Try again.");
      return;
    }

    try {
      const res = await fetch(`${backend}/auth/internal`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ passphrase: value }),
      });

      if (res.status === 401) { setBusy(false); fail("Invalid passphrase"); return; }
      // ⚠ /auth/internal is rate-limited 5/60s per IP (main.py:65) — the
      // brute-force oracle. A 429 is not a wrong passphrase and must not be
      // reported as one, or the user retypes a correct one into a closed door.
      if (res.status === 429) {
        setBusy(false);
        fail("Too many attempts. Wait a moment and try again.");
        return;
      }
      if (!res.ok) { setBusy(false); fail("Could not reach the platform. Try again."); return; }

      const data = await res.json();
      if (!data || !data.access_token) {
        setBusy(false);
        fail("Could not reach the platform. Try again.");
        return;
      }

      // ⚠⚠ qs_internal_token, NEVER qs_auth_token. That key belongs to the
      // Supabase flow: writing it here stomped a signed-in user's session, and
      // their next sign-in stomped this one straight back. Separate keys mean
      // the two identities can coexist and neither can destroy the other.
      localStorage.setItem(INTERNAL_TOKEN_KEY, data.access_token);
      localStorage.setItem("qs_tier", data.tier || "nous_topology");

      // ⚠⚠ A HARD RELOAD, NOT A REACT TRANSITION. App() reads the pathname once
      // at module-render time and AuthedApp resolves the session on mount; a
      // client-side swap would leave both holding the pre-login answer. Going
      // through the browser is what guarantees getInternalUser() runs again
      // against the token that now exists.
      // ⚠ busy is deliberately NOT cleared — the page is on its way out, and
      // re-enabling the form for the last frames invites a second submit.
      window.location.href = "/";
    } catch {
      setBusy(false);
      fail("Could not reach the platform. Try again.");
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: C.bg, fontFamily: font,
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, boxSizing: "border-box",
    }}>
      <form onSubmit={submit} style={{ width: "100%", maxWidth: 320 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
          <NousIcon size={48} color={C.amber} />
          <div style={{ color: C.text, fontSize: 22, fontWeight: 700,
                        letterSpacing: "0.18em", marginTop: 14 }}>
            QUANT SIGNAL
          </div>
          <div style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.12em",
                        marginTop: 4 }}>
            Internal Access
          </div>
        </div>

        <input
          ref={inputRef}
          type="password"
          placeholder="Passphrase"
          autoFocus
          maxLength={MAX_LEN}
          disabled={busy}
          value={value}
          onChange={(ev) => setValue(ev.target.value)}
          // ⚠ Autofill and password managers are told to stay out: this is a
          // shared operational credential, not a personal account password.
          autoComplete="off"
          spellCheck={false}
          style={{
            width: "100%", boxSizing: "border-box", marginTop: 34,
            background: "#111820", color: C.text, fontFamily: font, fontSize: 14,
            border: `1px solid ${C.border}`, borderRadius: 3,
            padding: "12px 12px", minHeight: 44, outline: "none",
          }}
        />

        <button
          type="submit"
          disabled={busy || !value}
          style={{
            ...s.btn("green"), width: "100%", boxSizing: "border-box",
            minHeight: 44, marginTop: 10, fontSize: 13, letterSpacing: "0.12em",
            opacity: busy || !value ? 0.55 : 1,
          }}
        >
          {busy ? "VERIFYING…" : "ENTER"}
        </button>

        {/* ⚠ One line, and only after an attempt. A permanent hint under an
            unlabelled field would tell a stranger what the field is for. */}
        {error && (
          <div style={{ color: C.red, fontSize: 11, marginTop: 10,
                        textAlign: "center" }}>
            {error}
          </div>
        )}
      </form>
    </div>
  );
}
